/**
 * The Agent Access & Protection demo fixture. One stable story that every
 * screen reads: a Customer Support Agent with two MCP services (20 tools), one
 * Snowflake data source carrying four native policies, one seeded policy gap
 * (Texas-only Row Access on CRM.CUSTOMERS), and a Snowflake MCP gateway path
 * that is configured but not yet verified.
 *
 * Counts are never stored: every summary is derived from these records.
 */
import type { AgentPolicyRecommendation } from "@/api/temp/AgentEstateService";
import type {
  AgentResourceLink,
  CatalogObject,
  GatewayPath,
  McpPolicy,
  NativePolicy,
  NativeRole,
  ProtectionExpectation,
  ProtectionRule,
  ProtectionTool,
  RegisteredDataSource,
  RegisteredMcpService,
} from "@/api/temp/AgentProtectionService";
import type { ConditionLeaf } from "@/components/ConditionBuilder/types";

/** The generated agent the fixture belongs to: `${environmentId}-customer-support`. */
export const SEEDED_AGENT_KEY = "customer-support";

export const isSeededAgentId = (agentId: string) =>
  agentId.endsWith(`-${SEEDED_AGENT_KEY}`);

/** Fixed demo clock, so timestamps never read as live data. */
export const SEED_NOW = "2026-10-14T10:42:00.000Z";
const DAY = 24 * 60 * 60 * 1000;
const daysAgo = (days: number) =>
  new Date(Date.parse(SEED_NOW) - days * DAY).toISOString();

export const IDS = {
  snowflakeMcp: "mcp-snowflake-customer",
  serviceNowMcp: "mcp-servicenow-tickets",
  sharePointMcp: "mcp-sharepoint-support-docs",
  analyticsMcp: "mcp-analytics-insights",
  customerDatabase: "ds-customer-database",
  supportAnalytics: "ds-support-analytics",
  supportArchive: "ds-support-archive",
  supportCrm: "ds-support-crm",
  agentAccessPolicy: "mcp-policy-customer-support",
  snowflakeServerPolicy: "mcp-policy-snowflake-server",
  texasExpectation: "exp-texas-customer-rows",
} as const;

/* -------------------------------------------------------------- MCP tools */

const tool = (
  id: string,
  name: string,
  description: string,
  classification: ProtectionTool["classification"],
  risk: ProtectionTool["risk"],
  dataSourceId?: string,
): ProtectionTool => ({
  id,
  name,
  description,
  classification,
  risk,
  ...(dataSourceId && { dataSourceId }),
});

const db = IDS.customerDatabase;

/** 12 tools, 2 of them high risk. */
const SNOWFLAKE_TOOLS: ProtectionTool[] = [
  tool("QueryCustomerData", "QueryCustomerData", "Query customer records", "Read", "Low", db),
  tool("GetCustomerProfile", "GetCustomerProfile", "Get customer profile details", "Read", "Low", db),
  tool("ListSupportCases", "ListSupportCases", "List support cases", "Read", "Low", db),
  tool("ExportCustomerData", "ExportCustomerData", "Export customer records in bulk", "Write", "High", db),
  tool("UpdateCustomerRecord", "UpdateCustomerRecord", "Update a customer record", "Write", "High", db),
  tool("GetOrderHistory", "GetOrderHistory", "Get a customer's order history", "Read", "Low", db),
  tool("SearchOrders", "SearchOrders", "Search orders by customer or status", "Read", "Low", db),
  tool("ListAccounts", "ListAccounts", "List customer accounts", "Read", "Low", db),
  tool("RunSavedQuery", "RunSavedQuery", "Run an approved saved query", "Read", "Medium", db),
  tool("DescribeTable", "DescribeTable", "Describe a table's columns", "Read", "Low"),
  tool("ListSchemas", "ListSchemas", "List available schemas", "Read", "Low"),
  tool("GetQueryHistory", "GetQueryHistory", "Get recent query history", "Read", "Low"),
];

/** 8 tools, 2 of them high risk. No recorded data source. */
const SERVICENOW_TOOLS: ProtectionTool[] = [
  tool("GetTicket", "GetTicket", "Get a support ticket", "Read", "Low"),
  tool("ListTickets", "ListTickets", "List open support tickets", "Read", "Low"),
  tool("SearchKnowledgeBase", "SearchKnowledgeBase", "Search approved support articles", "Read", "Low"),
  tool("CreateTicket", "CreateTicket", "Create a support ticket", "Write", "Medium"),
  tool("UpdateTicket", "UpdateTicket", "Update a support ticket", "Write", "Medium"),
  tool("AssignTicket", "AssignTicket", "Assign a ticket to a queue", "Write", "Low"),
  tool("CloseTicket", "CloseTicket", "Close a support ticket", "Write", "High"),
  tool("DeleteTicket", "DeleteTicket", "Delete a support ticket", "Destructive", "High"),
];

const SHAREPOINT_TOOLS: ProtectionTool[] = [
  tool("searchSites", "searchSites", "Search support SharePoint sites", "Read", "Low"),
  tool("searchDocuments", "searchDocuments", "Search support documents", "Read", "Low"),
  tool("getDocument", "getDocument", "Get a support document", "Read", "Low"),
  tool("listFolders", "listFolders", "List document folders", "Read", "Low"),
  tool("uploadDocument", "uploadDocument", "Upload a document", "Write", "Medium"),
  tool("shareDocument", "shareDocument", "Share a document externally", "Write", "High"),
];

const ANALYTICS_TOOLS: ProtectionTool[] = [
  tool("RunReport", "RunReport", "Run a support analytics report", "Read", "Low"),
  tool("ListDashboards", "ListDashboards", "List analytics dashboards", "Read", "Low"),
  tool("GetMetric", "GetMetric", "Get a support metric", "Read", "Low"),
  tool("ScheduleExport", "ScheduleExport", "Schedule a report export", "Write", "Medium"),
  tool("ShareReport", "ShareReport", "Share a report with a group", "Write", "Medium"),
];

/* --------------------------------------------------------------- registry */

/** Every MCP service registered in the tenant. */
export const MCP_REGISTRY: RegisteredMcpService[] = [
  {
    id: IDS.snowflakeMcp,
    name: "Snowflake MCP",
    technology: "Snowflake",
    platformLabel: "Snowflake Data Platform",
    description: "Access Snowflake datasets and perform queries",
    tools: SNOWFLAKE_TOOLS,
    dataSourceIds: [IDS.customerDatabase],
  },
  {
    id: IDS.serviceNowMcp,
    name: "ServiceNow MCP",
    technology: "ServiceNow",
    platformLabel: "ServiceNow Platform",
    description: "Access and manage ServiceNow records",
    tools: SERVICENOW_TOOLS,
    dataSourceIds: [],
  },
  {
    id: IDS.sharePointMcp,
    name: "SharePoint MCP",
    technology: "SharePoint",
    platformLabel: "Microsoft SharePoint",
    description: "Search and manage SharePoint sites",
    tools: SHAREPOINT_TOOLS,
    dataSourceIds: [],
  },
  {
    id: IDS.analyticsMcp,
    name: "Analytics MCP",
    technology: "Databricks",
    platformLabel: "Support analytics workspace",
    description: "Run support analytics reports and dashboards",
    tools: ANALYTICS_TOOLS,
    dataSourceIds: [],
  },
];

/** Every data source registered in the tenant. */
export const DATA_SOURCE_REGISTRY: RegisteredDataSource[] = [
  {
    id: IDS.customerDatabase,
    name: "Customer Database",
    platform: "Snowflake",
    platformId: "snowflake",
    environment: "Production",
    description: "Customer master and support records",
    nativePolicySupported: true,
  },
  {
    id: IDS.supportAnalytics,
    name: "Support Analytics",
    platform: "Databricks",
    platformId: "databricks",
    environment: "Production",
    description: "Support analytics warehouse",
    nativePolicySupported: true,
  },
  {
    id: IDS.supportArchive,
    name: "Support Archive",
    platform: "Snowflake",
    platformId: "snowflake",
    environment: "Production",
    description: "Historical support data",
    nativePolicySupported: true,
  },
  {
    id: IDS.supportCrm,
    name: "Support CRM",
    platform: "Salesforce",
    platformId: "salesforce",
    environment: "Production",
    description: "CRM production",
    nativePolicySupported: false,
  },
];

/* ---------------------------------------------------------------- catalog */

const col = (
  name: string,
  label: string,
  type: "STRING" | "NUMBER" | "DATE" = "STRING",
) => ({ name, label, type });

export const DATA_SOURCE_OBJECTS: Record<string, CatalogObject[]> = {
  [IDS.customerDatabase]: [
    {
      id: "CRM.CUSTOMERS",
      schema: "CRM",
      name: "CUSTOMERS",
      kind: "TABLE",
      columns: [
        col("CUSTOMER_ID", "Customer ID", "NUMBER"),
        col("FULL_NAME", "Full Name"),
        col("EMAIL", "Email"),
        col("PHONE", "Phone"),
        col("CUSTOMER_STATE", "Customer State"),
        col("REGION", "Region"),
        col("CREATED_AT", "Created At", "DATE"),
      ],
    },
    {
      id: "CRM.SUPPORT_CASES",
      schema: "CRM",
      name: "SUPPORT_CASES",
      kind: "TABLE",
      columns: [
        col("CASE_ID", "Case ID", "NUMBER"),
        col("CUSTOMER_ID", "Customer ID", "NUMBER"),
        col("CASE_COUNTRY", "Case Country"),
        col("STATUS", "Status"),
        col("OPENED_AT", "Opened At", "DATE"),
      ],
    },
    {
      id: "CRM.ANALYTICS_REPORTS",
      schema: "CRM",
      name: "ANALYTICS_REPORTS",
      kind: "VIEW",
      columns: [
        col("REPORT_ID", "Report ID", "NUMBER"),
        col("REGION", "Region"),
        col("METRIC", "Metric"),
        col("PERIOD", "Period", "DATE"),
      ],
    },
    {
      id: "CRM.ORDERS",
      schema: "CRM",
      name: "ORDERS",
      kind: "TABLE",
      columns: [
        col("ORDER_ID", "Order ID", "NUMBER"),
        col("CUSTOMER_ID", "Customer ID", "NUMBER"),
        col("ORDER_STATE", "Order State"),
        col("TOTAL", "Total", "NUMBER"),
      ],
    },
  ],
  [IDS.supportAnalytics]: [
    {
      id: "SUPPORT.TICKET_METRICS",
      schema: "SUPPORT",
      name: "TICKET_METRICS",
      kind: "TABLE",
      columns: [
        col("TEAM", "Team"),
        col("REGION", "Region"),
        col("RESOLUTION_HOURS", "Resolution Hours", "NUMBER"),
      ],
    },
  ],
  [IDS.supportArchive]: [
    {
      id: "ARCHIVE.CASES_2022",
      schema: "ARCHIVE",
      name: "CASES_2022",
      kind: "TABLE",
      columns: [
        col("CASE_ID", "Case ID", "NUMBER"),
        col("CUSTOMER_STATE", "Customer State"),
        col("SUMMARY", "Summary"),
      ],
    },
  ],
};

/** Native roles per platform. One Snowflake mapping is fixture-flagged stale. */
export const NATIVE_ROLES: Record<string, NativeRole[]> = {
  Snowflake: [
    {
      name: "CUSTOMER_SUPPORT_ROLE",
      stale: true,
      description:
        "Recorded in a 2023 mapping. The role was renamed and no longer exists in Snowflake.",
    },
    {
      name: "SNOWFLAKE_SUPPORT_ROLE",
      description: "Support role provisioned for customer-support services.",
    },
    {
      name: "SUPPORT_ROLE",
      description: "Shared support role used by several teams and agents.",
    },
    {
      name: "ANALYST_ROLE",
      description: "Analytics role with read access to CRM.",
    },
  ],
  Databricks: [
    {
      name: "support-analytics-readers",
      description: "Databricks group for support analytics readers.",
    },
  ],
};

/* ------------------------------------------------------------ conditions */

/** A condition leaf in the shape the Condition Builder reads and writes. */
export const conditionLeaf = (
  id: string,
  attribute: { id: string; name: string },
  value: string,
  operator: { id: string; name: string } = { id: "equals", name: "equals" },
): ConditionLeaf => ({
  id,
  attribute: { ...attribute, valueType: "text" },
  operator,
  value,
});

/* ------------------------------------------------------- native policies */

export const NATIVE_POLICIES: NativePolicy[] = [
  {
    id: "pol-row-support-cases",
    dataSourceId: db,
    type: "ROW_ACCESS",
    name: "US support-case access",
    description: "Support staff see support cases opened in the United States.",
    targetObjectIds: ["CRM.SUPPORT_CASES"],
    principal: {
      type: "ROLE",
      name: "SUPPORT_ROLE",
      mappingSource: "ADMINISTRATOR",
      verification: "VERIFIED",
    },
    combinator: "AND",
    conditions: [
      conditionLeaf(
        "cond-support-cases-country",
        { id: "CASE_COUNTRY", name: "Case Country" },
        "US",
      ),
    ],
    status: "DEPLOYED",
    updatedAt: daysAgo(6),
  },
  {
    id: "pol-mask-contact",
    dataSourceId: db,
    type: "DATA_MASKING",
    name: "Mask customer contact data",
    description: "Mask customer email and phone for analyst roles.",
    targetObjectIds: ["CRM.CUSTOMERS"],
    targetColumns: ["CRM.CUSTOMERS.EMAIL", "CRM.CUSTOMERS.PHONE"],
    principal: {
      type: "ROLE",
      name: "ANALYST_ROLE",
      mappingSource: "ADMINISTRATOR",
      verification: "VERIFIED",
    },
    combinator: "AND",
    conditions: [],
    status: "DEPLOYED",
    updatedAt: daysAgo(7),
  },
  {
    id: "pol-object-crm",
    dataSourceId: db,
    type: "OBJECT_ACCESS",
    name: "Customer object access",
    description: "Read access to the CRM schema.",
    targetObjectIds: ["CRM"],
    privileges: ["SELECT"],
    principal: {
      type: "ROLE",
      name: "ANALYST_ROLE",
      mappingSource: "ADMINISTRATOR",
      verification: "VERIFIED",
    },
    combinator: "AND",
    conditions: [],
    status: "DEPLOYED",
    updatedAt: daysAgo(9),
  },
  {
    id: "pol-row-analytics-draft",
    dataSourceId: db,
    type: "ROW_ACCESS",
    name: "Regional analytics rows",
    description: "Limit analytics report rows by region.",
    targetObjectIds: ["CRM.ANALYTICS_REPORTS"],
    principal: {
      type: "ROLE",
      name: "ANALYST_ROLE",
      mappingSource: "ADMINISTRATOR",
      verification: "NOT_VERIFIED",
    },
    combinator: "AND",
    conditions: [
      conditionLeaf(
        "cond-analytics-region",
        { id: "REGION", name: "Region" },
        "SOUTH",
      ),
    ],
    status: "DRAFT",
    updatedAt: daysAgo(2),
  },
];

/* ----------------------------------------------------------- MCP policies */

/**
 * The MCP policies that reach the seeded agent. Each rule names its owner, and
 * the owner decides where the rule is changed: the agent access policy in
 * Policy Management, the shared Snowflake server policy in MCP Services. Both
 * are read-only from Agent Details. Rules authored for the agent are authored
 * agent policies, not entries here.
 */
export const MCP_POLICIES: McpPolicy[] = [
  {
    id: IDS.agentAccessPolicy,
    name: "Customer Support Agent Access",
    ownership: "AGENT",
    managedIn: "POLICY_MANAGEMENT",
    appliesToAgentCount: 1,
    updatedAt: daysAgo(12),
  },
  {
    id: IDS.snowflakeServerPolicy,
    name: "Snowflake MCP Server Policy",
    ownership: "SHARED",
    managedIn: "MCP_SERVICES",
    appliesToAgentCount: 4,
    updatedAt: daysAgo(20),
  },
];

const rule = (input: Omit<ProtectionRule, "combinator" | "updatedAt">) =>
  ({
    combinator: "AND",
    deployment: "DEPLOYED",
    updatedAt: daysAgo(12),
    ...input,
  }) as ProtectionRule;

export const MCP_RULES: ProtectionRule[] = [
  rule({
    id: "rule-agent-support-conditions",
    policyId: IDS.agentAccessPolicy,
    name: "Customer support access conditions",
    description:
      "Allows access for requests made on behalf of the Customer Support team.",
    scope: "AGENT",
    toolIds: [],
    effect: "ALLOW",
    conditions: [
      conditionLeaf(
        "cond-agent-department",
        { id: "department", name: "Department" },
        "Customer Support",
      ),
    ],
  }),
  rule({
    id: "rule-server-snowflake-query",
    policyId: IDS.snowflakeServerPolicy,
    name: "Snowflake query access",
    description:
      "Allows Snowflake MCP queries during a support interaction. It does not limit which rows are returned.",
    scope: "SERVER",
    mcpServiceId: IDS.snowflakeMcp,
    toolIds: [],
    effect: "ALLOW",
    conditions: [
      conditionLeaf(
        "cond-server-purpose",
        { id: "request_purpose", name: "Request purpose" },
        "Support case",
      ),
    ],
  }),
];

/* ------------------------------------------------------------ expectation */

export const EXPECTATIONS: ProtectionExpectation[] = [
  {
    id: IDS.texasExpectation,
    title: "Missing Texas-only Row Access restriction for customer records",
    expected: "Restrict access to Texas (TX) customer records",
    reason:
      "The Customer Support Agent's Authorized territory attribute is Texas (TX): it may query customer records, but only for Texas customers.",
    authorization: {
      attribute: "Authorized territory",
      value: "Texas (TX)",
      scope: "Texas",
    },
    severity: "HIGH",
    dataSourceId: db,
    objectId: "CRM.CUSTOMERS",
    policyType: "ROW_ACCESS",
    condition: { attribute: "CUSTOMER_STATE", operator: "equals", value: "TX" },
    mcpServiceId: IDS.snowflakeMcp,
    toolId: "QueryCustomerData",
    suggestedPolicyName: "Texas customer access",
    suggestedPolicyDescription:
      "Restrict access to Texas customer records for support agents.",
  },
];

/* ---------------------------------------------------------------- links */

const discovered = (
  resourceKind: AgentResourceLink["resourceKind"],
  resourceId: string,
): AgentResourceLink => ({
  resourceKind,
  resourceId,
  origin: "DISCOVERED",
  verification: "NOT_VERIFIED",
});

export const LINKS: AgentResourceLink[] = [
  discovered("MCP_SERVICE", IDS.snowflakeMcp),
  discovered("MCP_SERVICE", IDS.serviceNowMcp),
  {
    ...discovered("DATA_SOURCE", IDS.customerDatabase),
    accessPath: "THROUGH_MCP",
    linkedMcpServiceIds: [IDS.snowflakeMcp],
  },
];

/* ------------------------------------------------------------- gateway */

export const GATEWAY_PATHS: GatewayPath[] = [
  {
    mcpServiceId: IDS.snowflakeMcp,
    setup: "CONFIGURED",
    verification: "PENDING",
  },
  {
    mcpServiceId: IDS.serviceNowMcp,
    setup: "CONFIGURED",
    verification: "VERIFIED",
    checkedAt: daysAgo(1),
  },
];

/** When the seeded assessment ran. */
export const SEED_ASSESSED_AT = daysAgo(0);

/* ------------------------------------------------------- recommendations */

/**
 * Recommendations for the seeded agent, in the existing recommendation shape so
 * the existing review and apply panels render them unchanged. Recommended
 * hardening only: none of them closes the Texas row-access gap.
 */
export interface SeededRecommendation {
  recommendation: Omit<AgentPolicyRecommendation, "appliesTo">;
  /** The tool rule applying the recommendation adds to the owning policy. */
  rule: {
    mcpServiceId: string;
    toolId: string;
    name: string;
    effect: ProtectionRule["effect"];
  };
}

export const RECOMMENDATIONS: SeededRecommendation[] = [
  {
    recommendation: {
      id: "rec-cs-block-bulk-export",
      name: "Block bulk customer export",
      outcome:
        "The agent keeps querying individual customer records, but can no longer export customer data in bulk.",
      whyRecommended:
        "ExportCustomerData is a high-risk write tool on Snowflake MCP. Exporting customer records in bulk is outside a support agent's purpose.",
      controls: [
        { sense: "ALLOW", label: "Query individual customer records" },
        { sense: "BLOCK", label: "Export customer data in bulk" },
      ],
      strength: "STRONGLY_RECOMMENDED",
      category: "TOOL_ACTION_CONTROLS",
      technology: "Snowflake",
      frameworks: ["SOC 2", "GDPR"],
      enforcement: {
        resource: "Snowflake MCP",
        toolGroups: ["Data export"],
        allowed: ["QueryCustomerData"],
        blocked: ["ExportCustomerData"],
        enforcementLayer: "MCP tool policy",
      },
    },
    rule: {
      mcpServiceId: IDS.snowflakeMcp,
      toolId: "ExportCustomerData",
      name: "Block bulk customer export",
      effect: "DENY",
    },
  },
  {
    recommendation: {
      id: "rec-cs-ticket-deletion",
      name: "Prevent ticket deletion",
      outcome:
        "The agent keeps creating and updating tickets, but cannot delete them.",
      whyRecommended:
        "DeleteTicket is a destructive ServiceNow tool. Support agents resolve and close tickets; they do not remove the record.",
      controls: [
        { sense: "ALLOW", label: "Create, update and close support tickets" },
        { sense: "BLOCK", label: "Delete support tickets" },
      ],
      strength: "RECOMMENDED",
      category: "TOOL_ACTION_CONTROLS",
      technology: "ServiceNow",
      frameworks: ["SOC 2"],
      advisory: true,
      enforcement: {
        resource: "ServiceNow MCP",
        toolGroups: ["Ticket lifecycle"],
        allowed: ["CreateTicket", "UpdateTicket", "CloseTicket"],
        blocked: ["DeleteTicket"],
        enforcementLayer: "MCP tool policy",
      },
    },
    rule: {
      mcpServiceId: IDS.serviceNowMcp,
      toolId: "DeleteTicket",
      name: "Prevent ticket deletion",
      effect: "DENY",
    },
  },
];
